// One vocabulary for where a metric's numbers come from. Every screen that
// names a source goes through here, so a raw key (`pco_list`) never shows.
// Mirrors the backend's apps/integrations/source_labels.py; the catalog's
// labels win when present, and these keep screens readable while it loads.
const ROCK = 'Rock RMS'
const PCO = 'Planning Center'
const CHURCHSTAQ = 'ChurchStaq'

/** Separator between a source's kind and its name or selection. */
export const LABEL_SEPARATOR = ' · '

/**
 * kind: what it is, without the provider (badges, entry notes).
 * label: provider + kind in sentence case (titles, pickers, tables).
 */
const SOURCE_LABELS = {
  rock_dataview: { kind: 'DataView', label: `${ROCK} DataView` },
  rock_activity: { kind: 'Group attendance', label: `${ROCK} group attendance` },
  rock_entities: { kind: 'DataView people', label: `${ROCK} DataView people` },
  rock_giving_donors: { kind: 'Giving donors', label: `${ROCK} giving donors` },
  rock_steps: { kind: 'Completed steps', label: `${ROCK} completed steps` },
  rock_metric: { kind: 'Rock metric', label: 'Rock metric', goal: 'Rock goals' },
  pco_list: { kind: 'List', label: `${PCO} list` },
  pco_people_count: { kind: 'People count', label: `${PCO} people count` },
  pco_activity: { kind: 'Check-ins', label: `${PCO} check-ins` },
  pco_groups: { kind: 'Group attendance', label: `${PCO} group attendance` },
  pco_membership: { kind: 'Group membership', label: `${PCO} group membership` },
  pco_attendance: { kind: 'Headcounts', label: `${PCO} headcounts` },
  pco_giving: { kind: 'Giving totals', label: `${PCO} giving totals` },
  pco_giving_donors: { kind: 'Giving donors', label: `${PCO} giving donors` },
  pco_service_times: { kind: 'Service times', label: `${PCO} service times` },
  pco_service_volunteers: { kind: 'Scheduled volunteers', label: `${PCO} scheduled volunteers` },
  churchstaq_occurrences: { kind: 'Event attendance', label: `${CHURCHSTAQ} event attendance` },
  churchstaq_groups: { kind: 'Active groups', label: `${CHURCHSTAQ} active groups` },
  churchstaq_schedules: { kind: 'Volunteers serving', label: `${CHURCHSTAQ} volunteers serving` },
}

/** Every source type this vocabulary names (the backend's SOURCE_* keys). */
export const KNOWN_SOURCE_TYPES = Object.freeze(Object.keys(SOURCE_LABELS))

// A lower_snake (or kebab) token such as `pco_list` - what a raw key looks like.
const RAW_KEY = /^[a-z][a-z0-9]*(?:[_-][a-z0-9]+)+$/

const friendly = (label, type) => typeof label === 'string' && label.trim() && label.trim() !== type && !RAW_KEY.test(label.trim())

/** `future_people_count` -> `Future people count`; never the raw key. */
export function humanizeKey(key) {
  const words = String(key ?? '').trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Data source'
}

/** True for a token that looks like an internal key rather than a name. */
export function isRawSourceKey(text) {
  return typeof text === 'string' && (Object.hasOwn(SOURCE_LABELS, text.trim()) || RAW_KEY.test(text.trim()))
}

/** Never infer availability of a flagged provider from a retained connection. */
export function isSourceTypeVisible(type, providers = []) {
  if (typeof type !== 'string' || !type.startsWith('churchstaq_')) return true
  return providers.some((provider) => provider.id === 'churchstaq'
    && (provider.source_types?.includes(type) || friendly(provider.source_type_labels?.[type], type)))
}

/**
 * A source type's name. `label` (default): provider + kind from the catalog
 * or this table ("Planning Center list"); `kind`/`badge`: the kind alone
 * ("List"); `goal`: Rock goals. Unknown keys are humanized, never shown raw.
 */
export function sourceTypeLabel(type, providers = [], context = 'label') {
  if (!isSourceTypeVisible(type, providers)) return 'Unavailable source'
  if (typeof type !== 'string' || !type.trim()) return 'Data source'
  const local = SOURCE_LABELS[type]
  if (context === 'badge' || context === 'kind') return local?.kind || humanizeKey(type)
  if (context !== 'label' && local?.[context]) return local[context]
  const catalogLabel = providers.map((provider) => provider.source_type_labels?.[type]).find((label) => friendly(label, type))
  if (catalogLabel) return catalogLabel.trim()
  return local?.label || humanizeKey(type)
}

const plural = (count, singular, many) => `${count} ${count === 1 ? singular : many}`
const countOf = (values, singular, many) => (Array.isArray(values) && values.length ? plural(values.length, singular, many) : null)

const SELECTION_NOUNS = [
  ['group_type_ids', 'group type', 'group types'],
  ['group_ids', 'group', 'groups'],
  ['event_ids', 'event', 'events'],
  ['service_type_ids', 'service type', 'service types'],
  ['step_type_ids', 'step type', 'step types'],
]
const OPTIONAL_SELECTIONS = {
  pco_giving: ['fund_ids', 'fund', 'funds', 'All funds'],
  pco_giving_donors: ['fund_ids', 'fund', 'funds', 'All funds'],
  rock_giving_donors: ['account_ids', 'account', 'accounts', 'All accounts'],
}

/** What a nameless source selects: "3 groups", "1 group type", "All events"; null for none. */
export function sourceSelectionSummary(type, config) {
  if (!config || typeof config !== 'object') return null
  if (config.event_scope === 'all') return 'All events'
  for (const [key, singular, many] of SELECTION_NOUNS) {
    const summary = countOf(config[key], singular, many)
    if (summary) return summary
  }
  const optional = OPTIONAL_SELECTIONS[type]
  if (!optional) return null
  const [key, singular, many, everything] = optional
  return countOf(config[key], singular, many) || everything
}

const text = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null)

const ID_ONLY = /^#?\d+$/

/**
 * An older backend's `source_label` when it is a real name: before
 * `source_name` existed it fell back to the provider ID or the raw key.
 */
function legacyLabelName(source) {
  if (!source || Object.hasOwn(source, 'source_name')) return null
  const label = text(source.source_label)
  if (!label || label === source.source_type || isRawSourceKey(label) || ID_ONLY.test(label)) return null
  // A newer backend's label may be the provider + kind itself, never a name.
  return label.startsWith(sourceTypeLabel(source.source_type)) ? null : label
}

/** The stored name of the chosen list, group, DataView or event, if any. */
export function sourceName(source) {
  const config = source?.source_config || {}
  return text(source?.source_name) || text(config.source_name) || text(config.event_name) || legacyLabelName(source)
}

/**
 * Plain-language provenance for a pipeline or metric data source:
 * `{ title: 'Planning Center list', kind: 'List', name, selection, detail,
 *    label: 'Planning Center list · First-time guests' }`.
 */
export function describeSource(source, providers = []) {
  const type = source?.source_type
  const title = sourceTypeLabel(type, providers)
  const kind = sourceTypeLabel(type, providers, 'kind')
  const name = sourceName(source)
  const selection = sourceSelectionSummary(type, source?.source_config)
  const detail = name || selection
  return { title, kind, name, selection, detail, label: detail ? `${title}${LABEL_SEPARATOR}${detail}` : title }
}

const ARROW = ' ← '

/** A stored pipeline name, with an auto-name's trailing raw key ("Guests ← pco_list") made readable. */
export function displayPipelineName(name, providers = []) {
  if (typeof name !== 'string' || !name.trim()) return 'Data source'
  const index = name.lastIndexOf(ARROW)
  if (index < 0) return isRawSourceKey(name) ? sourceTypeLabel(name.trim(), providers) : name
  const tail = name.slice(index + ARROW.length)
  return isRawSourceKey(tail) ? `${name.slice(0, index)}${ARROW}${sourceTypeLabel(tail.trim(), providers)}` : name
}

// Synced entries' notes read "<provider> - <detail>" (apps/integrations/source_labels.entry_notes).
// [stamped prefix, provider key, display name]
const NOTE_PROVIDERS = [['Planning Center', 'planning_center', PCO], ['RockRMS', 'rockrms', ROCK], ['ChurchStaq', 'churchstaq', CHURCHSTAQ]]

/**
 * A synced entry's notes split into its provider and a readable detail:
 * "Planning Center - pco_list" -> { provider: 'planning_center', detail: 'List' }.
 * Older entries stamped the raw key; newer ones already read "List · Guests".
 * Returns null for notes a person typed.
 */
export function parseEntrySourceNote(notes) {
  if (typeof notes !== 'string') return null
  for (const [prefix, provider, providerName] of NOTE_PROVIDERS) {
    const match = notes.match(new RegExp(`^${prefix}\\s+-\\s*(.*)$`, 's'))
    if (!match) continue
    const tail = match[1].trim()
    // The entry exists, so its source was visible when it synced: no catalog gate here.
    const detail = isRawSourceKey(tail) ? SOURCE_LABELS[tail]?.kind || humanizeKey(tail) : tail
    return { provider, providerName, detail: detail || sourceTypeLabel(null) }
  }
  return null
}

/** Entry notes for display: a synced entry's raw source key becomes its kind. */
export function displayEntryNotes(notes) {
  const parsed = parseEntrySourceNote(notes)
  return parsed ? `${parsed.providerName} - ${parsed.detail}` : notes
}
